import type React from 'react';
import { useTheme, type ThemeTokens } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';

export interface CourseCompleteBannerProps {
  onDownloadCertificate?: () => void;
  certificateEnabled?: boolean;
  isDownloadingCertificate?: boolean;
  /** User-facing message shown when a certificate download attempt failed. */
  certificateError?: string | null;
  style?: React.CSSProperties;
}

export function CourseCompleteBanner({
  onDownloadCertificate,
  certificateEnabled = true,
  isDownloadingCertificate = false,
  certificateError = null,
  style,
}: CourseCompleteBannerProps): React.ReactElement {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const styles = createStyles(tokens);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{ ...styles.banner, ...style }}
      data-testid="course-complete-banner"
    >
      <div style={styles.row}>
        <BurstIcon color={tokens.text.onBrand} />
        <span style={styles.title}>{t('course_complete_banner.title')}</span>
        {certificateEnabled ? (
          <button
            type="button"
            style={{
              ...styles.button,
              ...(isDownloadingCertificate ? styles.buttonDisabled : {}),
            }}
            onClick={() => {
              onDownloadCertificate?.();
            }}
            disabled={isDownloadingCertificate}
            aria-busy={isDownloadingCertificate}
            data-testid="course-complete-banner-download-cert"
          >
            {isDownloadingCertificate
              ? t('course_complete_banner.downloading_certificate')
              : t('course_complete_banner.download_certificate')}
          </button>
        ) : null}
      </div>
      {certificateError ? (
        <div role="alert" style={styles.error} data-testid="course-complete-banner-cert-error">
          {certificateError}
        </div>
      ) : null}
    </div>
  );
}

CourseCompleteBanner.displayName = 'CourseCompleteBanner';

// Burst/sparkle glyph — exact vector from Figma (node 3987:711). `fill` follows
// the `color` prop (text.onBrand) so it stays recolorable via the theme.
function BurstIcon({ color }: { color: string }): React.ReactElement {
  return (
    <svg
      width={23}
      height={23}
      viewBox="0 0 23 23"
      role="img"
      aria-hidden="true"
      style={{ flex: 'none' }}
    >
      <path
        d="M9.42079 0.31124C9.16221 0.0484239 8.78494 -0.0575504 8.42887 0.0399459C8.07279 0.137442 7.79726 0.412975 7.70824 0.769049L7.05968 3.32091C7.01305 3.50742 6.8223 3.61764 6.64002 3.56253L4.10512 2.85038C3.74904 2.74865 3.36754 2.85038 3.10896 3.10896C2.85038 3.36754 2.74865 3.74904 2.85038 4.10512L3.56677 6.64002C3.61764 6.8223 3.50742 7.01305 3.32515 7.05968L0.769049 7.70824C0.412975 7.79726 0.133203 8.07703 0.0357069 8.43311C-0.0617894 8.78918 0.0441849 9.16645 0.307001 9.42503L2.19334 11.2605C2.32899 11.3919 2.32899 11.6123 2.19334 11.748L0.31124 13.5835C0.0484239 13.842 -0.0575504 14.2193 0.0399459 14.5754C0.137442 14.9314 0.417214 15.207 0.773288 15.3002L3.32515 15.9488C3.51166 15.9954 3.62188 16.1862 3.56677 16.3685L2.85038 18.8991C2.74865 19.2552 2.85038 19.6367 3.10896 19.8953C3.36754 20.1539 3.74904 20.2556 4.10512 20.1539L6.64002 19.4375C6.8223 19.3866 7.01305 19.4968 7.05968 19.6791L7.70824 22.231C7.79726 22.587 8.07703 22.8668 8.43311 22.9643C8.78918 23.0618 9.16645 22.9558 9.42503 22.693L11.2605 20.8067C11.3919 20.671 11.6123 20.671 11.748 20.8067L13.5792 22.693C13.8378 22.9558 14.2151 23.0618 14.5711 22.9643C14.9272 22.8668 15.2027 22.587 15.296 22.231L15.9446 19.6833C15.9912 19.4968 16.1819 19.3866 16.3642 19.4417L18.8991 20.1581C19.2552 20.2598 19.6367 20.1581 19.8953 19.8995C20.1539 19.6409 20.2556 19.2594 20.1539 18.9034L19.4375 16.3685C19.3866 16.1862 19.4968 15.9954 19.6791 15.9488L22.231 15.3002C22.587 15.2112 22.8668 14.9314 22.9643 14.5754C23.0618 14.2193 22.9558 13.8378 22.693 13.5835L20.8067 11.748C20.671 11.6166 20.671 11.3961 20.8067 11.2605L22.693 9.42503C22.9558 9.16645 23.0618 8.78918 22.9643 8.43311C22.8668 8.07703 22.587 7.8015 22.231 7.70824L19.6791 7.05968C19.4926 7.01305 19.3824 6.8223 19.4375 6.64002L20.1539 4.10512C20.2556 3.74904 20.1539 3.36754 19.8953 3.10896C19.6367 2.85038 19.2552 2.74865 18.8991 2.85038L16.3642 3.56677C16.1819 3.61764 15.9912 3.50742 15.9446 3.32515L15.296 0.769049C15.207 0.412975 14.9272 0.133203 14.5711 0.0357069C14.2151 -0.0617894 13.8378 0.0441849 13.5792 0.307001L11.7437 2.19758C11.6123 2.33323 11.3919 2.33323 11.2563 2.19758L9.42079 0.31124Z"
        fill={color}
      />
    </svg>
  );
}

function createStyles(tokens: ThemeTokens): Record<string, React.CSSProperties> {
  return {
    banner: {
      width: '100%',
      // Always the fixed completion green (tokens.completion.accent) — matches
      // the "Completed" top border on the assignment-panel sidebar. Reverses
      // the NA-240 decision to use the account's brand.accent here.
      backgroundColor: tokens.completion.accent,
      padding: '15px',
      borderRadius: '5px',
      boxShadow: '0px 4px 2px rgba(0,0,0,0.15)',
      boxSizing: 'border-box',
      fontFamily: tokens.typography.fontFamily,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
    },
    title: {
      flex: 1,
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.bold,
      lineHeight: 'normal',
      color: tokens.text.onBrand,
    },
    button: {
      backgroundColor: tokens.text.onBrand,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: 'normal',
      padding: '5px 20px',
      borderRadius: '20px',
      border: 'none',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    buttonDisabled: {
      cursor: 'default',
      opacity: 0.6,
    },
    error: {
      marginTop: '8px',
      fontSize: tokens.typography.sizes.base,
      lineHeight: 'normal',
      color: tokens.text.onBrand,
    },
  };
}
